import { useMemo } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  LabelList,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import type { PassengerDemandView } from '../../../domain/models/contracts';
import { buildWaterfall } from './routeMetrics';
import { CHART_COLORS, TOOLTIP_STYLE, WATERFALL_COLORS } from './chartTheme';
import { formatInt, formatPct } from './formatters';

interface DemandWaterfallProps {
  readonly demand: PassengerDemandView;
}

export function DemandWaterfall({ demand }: DemandWaterfallProps) {
  const bars = useMemo(() => buildWaterfall(demand), [demand]);
  const captureRatePct =
    demand.totalPotential > 0 ? (demand.carried / demand.totalPotential) * 100 : 0;

  return (
    <section className="ri-panel" aria-label="Demand waterfall">
      <header className="ri-panel__header">
        <h2 className="ri-panel__title">Demand Waterfall</h2>
        <p className="ri-panel__subtitle">
          Potential O&amp;D demand vs carried and unmet passengers ·{' '}
          <strong>{formatPct(captureRatePct)}</strong> captured
        </p>
      </header>

      <div className="ri-chart" role="img" aria-label="Bar chart of potential, unmet and carried passengers">
        <ResponsiveContainer width="100%" height={240}>
          <BarChart data={[...bars]} margin={{ top: 22, right: 16, bottom: 4, left: 4 }}>
            <CartesianGrid stroke={CHART_COLORS.grid} vertical={false} />
            <XAxis
              dataKey="label"
              stroke={CHART_COLORS.axis}
              tick={{ fill: CHART_COLORS.axis, fontSize: 12 }}
              tickLine={false}
            />
            <YAxis
              stroke={CHART_COLORS.axis}
              tick={{ fill: CHART_COLORS.axis, fontSize: 12 }}
              tickLine={false}
              axisLine={false}
              width={56}
              tickFormatter={(value: number) => formatInt(value)}
            />
            <Tooltip
              cursor={{ fill: 'rgba(255, 255, 255, 0.04)' }}
              contentStyle={TOOLTIP_STYLE}
              labelStyle={{ color: CHART_COLORS.text }}
              itemStyle={{ color: CHART_COLORS.text }}
              formatter={(value: unknown, name: unknown): [string, string] => [
                typeof value === 'number' ? formatInt(value) : String(value),
                typeof name === 'string' ? name : '',
              ]}
            />
            {/* Invisible spacer that lets the "unmet" bar float down from the top. */}
            <Bar
              dataKey="base"
              stackId="waterfall"
              fill="transparent"
              isAnimationActive={false}
              tooltipType="none"
            />
            <Bar dataKey="value" name="Passengers" stackId="waterfall" isAnimationActive={false} radius={[4, 4, 0, 0]}>
              {bars.map((bar) => (
                <Cell key={bar.id} fill={WATERFALL_COLORS[bar.id]} />
              ))}
              <LabelList
                dataKey="value"
                position="top"
                fill={CHART_COLORS.text}
                fontSize={12}
                formatter={(label: unknown) =>
                  typeof label === 'number' ? formatInt(label) : ''
                }
              />
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </section>
  );
}
